import { Controller } from "@hotwired/stimulus"
import { gsap } from "gsap"

// Splits the element's text into word + char spans and reveals them with a
// staggered GSAP timeline as soon as the element enters the viewport.
//
// data-text-reveal-mode-value: "char" | "word" (default "word")
// data-text-reveal-stagger-value: per-token delay in seconds (default 0.025)
// data-text-reveal-y-value: vertical travel in em (default 1.1)
export default class extends Controller {
  static values = {
    mode:    { type: String, default: "word" },
    stagger: { type: Number, default: 0.025 },
    y:       { type: Number, default: 1.1 }
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    this._split()
    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this._reveal()
          this._observer.unobserve(entry.target)
        }
      })
    }, { threshold: 0.25 })
    this._observer.observe(this.element)
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
  }

  _split() {
    // Preserve original HTML so we don't strip nested spans (e.g., figma-frames).
    const tokens = []
    const walk = (node, parent) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent
          // Walk through the text, emitting word spans + literal space text nodes.
          const re = /(\S+)(\s*)/g
          const frag = document.createDocumentFragment()
          let m
          while ((m = re.exec(text)) !== null) {
            const wordWrap = document.createElement("span")
            wordWrap.className = "tr-word"
            wordWrap.style.display = "inline-block"
            wordWrap.style.overflow = "hidden"
            wordWrap.style.verticalAlign = "top"
            const word = document.createElement("span")
            word.className = "tr-inner"
            word.style.display = "inline-block"
            word.style.willChange = "transform, opacity"
            word.style.transform = `translateY(${this.yValue}em)`
            word.style.opacity = "0"
            word.textContent = m[1]
            wordWrap.appendChild(word)
            frag.appendChild(wordWrap)
            if (m[2]) frag.appendChild(document.createTextNode(m[2]))
            tokens.push(word)
          }
          parent.replaceChild(frag, child)
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walk(child, child)
        }
      })
    }
    walk(this.element, this.element)
    this._tokens = tokens
  }

  _reveal() {
    gsap.to(this._tokens, {
      y: 0,
      opacity: 1,
      duration: 0.9,
      ease: "expo.out",
      stagger: this.staggerValue
    })
  }
}
